import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import CategoryManager from "../components/CategoryManager";
import BulkPortalInviteModal from "../components/BulkPortalInviteModal";
import PortalStatusBadge from "../components/PortalStatusBadge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import {
  Plus,
  Sparkles,
  Search,
  Pencil,
  Trash2,
  Users,
  Loader2,
  UserCheck,
  UserX,
  Award,
  Settings2,
  X,
  Mail,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { EMPLOYEE_TYPES, formatDate } from "../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Employees = () => {
  const { isOfficeManager } = useAuth();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCurrentOnly, setShowCurrentOnly] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({
    // Basic Info
    name: "", // Legal name (required)
    preferred_name: "",
    title: "", // Mr, Mrs, Ms, Dr, etc.
    
    // Contact
    email: "",
    phone: "",
    phone_extension: "",
    fax: "",
    cell_phone: "",
    emergency_contact_name: "",
    emergency_contact_phone: "",
    emergency_contact_relationship: "",
    
    // Address
    address: "",
    unit: "",
    city: "",
    province: "",
    postal_code: "",
    country: "Canada",
    
    // Employment
    employee_type: "lowrise_crew",
    hire_date: "",
    department: "",
    job_title: "",
    manager_id: "",
    
    // Vehicle
    vehicle_signed_out: "",
    personal_vehicle: false,
    
    // Certifications & Licenses (dynamic array)
    certificates: [],
    work_visa: false,
    
    // Pay & Benefits
    hourly_rate: 0,
    salary: 0,
    direct_deposit: false,
    banked_hours: 0,
    company_shares: 0,
    gas_allowance: 0,
    sick_days_used: 0,
    sick_days_total: 10,
    vacation_days_used: 0,
    vacation_days_total: 15,
    
    // Banking (for payroll)
    banking_info: {
      bank_name: "",
      account_holder: "",
      account_number: "",
      transit_number: "",
      institution_number: ""
    },
    
    // Personal Info
    date_of_birth: "",
    sin: "", // Social Insurance Number - sensitive
    allergies: "",
    medical_conditions: "",
    dietary_restrictions: "",
    
    // Custom sections for additional info
    custom_sections: [],
    
    // Custom
    notes: "",
    custom_fields: {},
    
    // Phase 19.23 — Portal access invitation
    grant_portal_access: false,
    send_invite_now: null,  // null = honor tenant default
  });
  const [saving, setSaving] = useState(false);
  // Phase 19.23 — bulk portal invite
  const [bulkInviteOpen, setBulkInviteOpen] = useState(false);
  const [autoSendInvitesDefault, setAutoSendInvitesDefault] = useState(false);

  const fetchInvitePreferences = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/portal/invite-preferences`);
      setAutoSendInvitesDefault(!!response.data?.auto_send_portal_invites);
    } catch (error) {
      // Not critical
    }
  }, []);

  useEffect(() => {
    fetchInvitePreferences();
  }, [fetchInvitePreferences]);

  useEffect(() => {
    fetchEmployees();
    
    // Check for quick action intent
    const quickAction = sessionStorage.getItem('quickAction');
    if (quickAction === 'add-employee') {
      sessionStorage.removeItem('quickAction');
      resetForm();
      setDialogOpen(true);
    }
  }, [showCurrentOnly]);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(
        `${API}/employees?current_only=${showCurrentOnly}`
      );
      setEmployees(response.data);
    } catch (error) {
      toast.error("Failed to fetch employees");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      // Basic Info
      name: "",
      preferred_name: "",
      title: "",
      
      // Contact
      email: "",
      phone: "",
      phone_extension: "",
      fax: "",
      cell_phone: "",
      emergency_contact_name: "",
      emergency_contact_phone: "",
      emergency_contact_relationship: "",
      
      // Address
      address: "",
      unit: "",
      city: "",
      province: "",
      postal_code: "",
      country: "Canada",
      
      // Employment
      employee_type: "lowrise_crew",
      hire_date: "",
      department: "",
      job_title: "",
      manager_id: "",
      
      // Vehicle
      vehicle_signed_out: "",
      personal_vehicle: false,
      
      // Certifications & Licenses
      certificates: [],
      work_visa: false,
      
      // Pay & Benefits
      hourly_rate: 0,
      salary: 0,
      direct_deposit: false,
      banked_hours: 0,
      company_shares: 0,
      gas_allowance: 0,
      sick_days_used: 0,
      sick_days_total: 10,
      vacation_days_used: 0,
      vacation_days_total: 15,
      
      // Banking
      banking_info: {
        bank_name: "",
        account_holder: "",
        account_number: "",
        transit_number: "",
        institution_number: ""
      },
      
      // Personal Info
      date_of_birth: "",
      sin: "",
      allergies: "",
      medical_conditions: "",
      dietary_restrictions: "",
      
      // Custom sections
      custom_sections: [],
      
      // Custom
      notes: "",
      custom_fields: {},
    });
    setEditingEmployee(null);
  };

  const handleOpenDialog = (employee = null) => {
    if (employee) {
      setEditingEmployee(employee);
      setFormData({
        name: employee.name || "",
        preferred_name: employee.preferred_name || "",
        title: employee.title || "",
        email: employee.email || "",
        phone: employee.phone || "",
        phone_extension: employee.phone_extension || "",
        fax: employee.fax || "",
        cell_phone: employee.cell_phone || "",
        emergency_contact_name: employee.emergency_contact_name || "",
        emergency_contact_phone: employee.emergency_contact_phone || "",
        emergency_contact_relationship: employee.emergency_contact_relationship || "",
        address: employee.address || "",
        unit: employee.unit || "",
        city: employee.city || "",
        province: employee.province || "",
        postal_code: employee.postal_code || "",
        country: employee.country || "Canada",
        employee_type: employee.employee_type || "lowrise_crew",
        hire_date: employee.hire_date || "",
        department: employee.department || "",
        job_title: employee.job_title || "",
        manager_id: employee.manager_id || "",
        vehicle_signed_out: employee.vehicle_signed_out || "",
        personal_vehicle: employee.personal_vehicle || false,
        certificates: employee.certificates || [],
        work_visa: employee.work_visa || false,
        hourly_rate: employee.hourly_rate || 0,
        salary: employee.salary || 0,
        direct_deposit: employee.direct_deposit || false,
        banked_hours: employee.banked_hours || 0,
        company_shares: employee.company_shares || 0,
        gas_allowance: employee.gas_allowance || 0,
        sick_days_used: employee.sick_days_used || 0,
        sick_days_total: employee.sick_days_total || 10,
        vacation_days_used: employee.vacation_days_used || 0,
        vacation_days_total: employee.vacation_days_total || 15,
        banking_info: employee.banking_info || {
          bank_name: "",
          account_holder: "",
          account_number: "",
          transit_number: "",
          institution_number: ""
        },
        date_of_birth: employee.date_of_birth || "",
        sin: employee.sin || "",
        allergies: employee.allergies || "",
        medical_conditions: employee.medical_conditions || "",
        dietary_restrictions: employee.dietary_restrictions || "",
        custom_sections: employee.custom_sections || [],
        notes: employee.notes || "",
        custom_fields: employee.custom_fields || {},
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  // Certificate handlers
  const addCertificate = () => {
    const newCert = {
      id: Date.now().toString(),
      type: "",
      number: "",
      issue_date: "",
      expiry_date: "",
    };
    setFormData({
      ...formData,
      certificates: [...(formData.certificates || []), newCert],
    });
  };

  const updateCertificate = (index, field, value) => {
    const certs = [...(formData.certificates || [])];
    certs[index] = { ...certs[index], [field]: value };
    setFormData({ ...formData, certificates: certs });
  };

  const removeCertificate = (index) => {
    const certs = [...(formData.certificates || [])];
    certs.splice(index, 1);
    setFormData({ ...formData, certificates: certs });
  };

  // Custom section handlers
  const addCustomSection = () => {
    const newSection = {
      id: Date.now().toString(),
      name: "",
      fields: [],
    };
    setFormData({
      ...formData,
      custom_sections: [...(formData.custom_sections || []), newSection],
    });
  };

  const updateSectionName = (index, name) => {
    const sections = [...(formData.custom_sections || [])];
    sections[index] = { ...sections[index], name };
    setFormData({ ...formData, custom_sections: sections });
  };

  const removeSection = (index) => {
    const sections = [...(formData.custom_sections || [])];
    sections.splice(index, 1);
    setFormData({ ...formData, custom_sections: sections });
  };

  const addFieldToSection = (sectionIndex) => {
    const sections = [...(formData.custom_sections || [])];
    const newField = {
      id: Date.now().toString(),
      label: "",
      value: "",
    };
    sections[sectionIndex] = {
      ...sections[sectionIndex],
      fields: [...(sections[sectionIndex].fields || []), newField],
    };
    setFormData({ ...formData, custom_sections: sections });
  };

  const updateSectionField = (sectionIndex, fieldIndex, key, value) => {
    const sections = [...(formData.custom_sections || [])];
    const fields = [...sections[sectionIndex].fields];
    fields[fieldIndex] = { ...fields[fieldIndex], [key]: value };
    sections[sectionIndex] = { ...sections[sectionIndex], fields };
    setFormData({ ...formData, custom_sections: sections });
  };

  const removeSectionField = (sectionIndex, fieldIndex) => {
    const sections = [...(formData.custom_sections || [])];
    const fields = [...sections[sectionIndex].fields];
    fields.splice(fieldIndex, 1);
    sections[sectionIndex] = { ...sections[sectionIndex], fields };
    setFormData({ ...formData, custom_sections: sections });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingEmployee) {
        await axios.put(`${API}/employees/${editingEmployee.id}`, formData);
        toast.success("Employee updated");
      } else {
        await axios.post(`${API}/employees`, formData);
        toast.success("Employee created");
      }
      setDialogOpen(false);
      resetForm();
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to archive this employee?"))
      return;

    try {
      await axios.delete(`${API}/employees/${id}`);
      toast.success("Employee archived");
      fetchEmployees();
    } catch (error) {
      toast.error("Failed to archive employee");
    }
  };

  const filteredEmployees = employees.filter(
    (emp) =>
      emp.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="employees-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">
            Employees
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage your team members and their information.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isOfficeManager && (
            <>
              <Button
                variant="outline"
                onClick={() => setBulkInviteOpen(true)}
                data-testid="send-invites-btn"
                className="flex-1 sm:flex-initial"
              >
                <Mail className="h-4 w-4 mr-2" />
                Send Invites
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/bulk-upload?kind=employee')}
                data-testid="bulk-add-employees-btn"
                className="flex-1 sm:flex-initial"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Bulk Add
              </Button>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={() => handleOpenDialog()} data-testid="add-employee-btn" className="flex-1 sm:flex-initial">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Employee
                  </Button>
                </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingEmployee ? "Edit Employee" : "Add New Employee"}
                  </DialogTitle>
                  <DialogDescription>
                    Fill in the employee details below.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <Tabs defaultValue="basic" className="w-full">
                    <TabsList className="grid w-full grid-cols-4">
                      <TabsTrigger value="basic">Basic</TabsTrigger>
                      <TabsTrigger value="contact">Contact</TabsTrigger>
                      <TabsTrigger value="employment">Employment</TabsTrigger>
                      <TabsTrigger value="personal">Personal</TabsTrigger>
                    </TabsList>

                    {/* Basic Info Tab */}
                    <TabsContent value="basic" className="space-y-4 mt-4">
                      <div className="grid grid-cols-4 gap-4">
                        <div className="space-y-2">
                          <Label>Title</Label>
                          <Select
                            value={formData.title || "none"}
                            onValueChange={(v) => setFormData({ ...formData, title: v === "none" ? "" : v })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">None</SelectItem>
                              <SelectItem value="Mr">Mr.</SelectItem>
                              <SelectItem value="Mrs">Mrs.</SelectItem>
                              <SelectItem value="Ms">Ms.</SelectItem>
                              <SelectItem value="Dr">Dr.</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2 space-y-2">
                          <Label htmlFor="name">Full Name (Legal) *</Label>
                          <Input
                            id="name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            required
                            data-testid="employee-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Preferred Name</Label>
                          <Input
                            value={formData.preferred_name}
                            onChange={(e) => setFormData({ ...formData, preferred_name: e.target.value })}
                            placeholder="What to call them"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="email">Email *</Label>
                          <Input
                            id="email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            required
                            data-testid="employee-email-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="employee_type">Type *</Label>
                          <CategoryManager
                            categoryType="employee"
                            label="Employee Type"
                            onCategoriesChange={fetchEmployees}
                            showAsDropdown={true}
                            selectedValue={formData.employee_type}
                            onSelectionChange={(val) => setFormData({ ...formData, employee_type: val })}
                            allowAll={false}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="hire_date">Hire Date</Label>
                          <Input
                            id="hire_date"
                            type="date"
                            value={formData.hire_date}
                            onChange={(e) => setFormData({ ...formData, hire_date: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Department</Label>
                          <Input
                            value={formData.department}
                            onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                            placeholder="e.g., Operations"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Job Title</Label>
                        <Input
                          value={formData.job_title}
                          onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                          placeholder="e.g., Senior Technician"
                        />
                      </div>
                    </TabsContent>

                    {/* Contact Tab */}
                    <TabsContent value="contact" className="space-y-4 mt-4">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Phone</Label>
                          <Input
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            placeholder="Main phone"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Extension</Label>
                          <Input
                            value={formData.phone_extension}
                            onChange={(e) => setFormData({ ...formData, phone_extension: e.target.value })}
                            placeholder="Ext"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Cell Phone</Label>
                          <Input
                            value={formData.cell_phone}
                            onChange={(e) => setFormData({ ...formData, cell_phone: e.target.value })}
                            placeholder="Mobile number"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Fax</Label>
                        <Input
                          value={formData.fax}
                          onChange={(e) => setFormData({ ...formData, fax: e.target.value })}
                          placeholder="Fax number"
                        />
                      </div>

                      {/* Address */}
                      <div className="border rounded-lg p-4 space-y-4">
                        <h4 className="font-medium text-sm">Address</h4>
                        <div className="grid grid-cols-3 gap-4">
                          <div className="col-span-2 space-y-2">
                            <Label>Street Address</Label>
                            <Input
                              value={formData.address}
                              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                              placeholder="123 Main St"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Unit/Apt</Label>
                            <Input
                              value={formData.unit}
                              onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                              placeholder="Unit #"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-4 gap-4">
                          <div className="space-y-2">
                            <Label>City</Label>
                            <Input
                              value={formData.city}
                              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Province</Label>
                            <Input
                              value={formData.province}
                              onChange={(e) => setFormData({ ...formData, province: e.target.value })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Postal Code</Label>
                            <Input
                              value={formData.postal_code}
                              onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Country</Label>
                            <Input
                              value={formData.country}
                              onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Emergency Contact */}
                      <div className="border rounded-lg p-4 space-y-4">
                        <h4 className="font-medium text-sm">Emergency Contact</h4>
                        <div className="grid grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label>Name</Label>
                            <Input
                              value={formData.emergency_contact_name}
                              onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
                              placeholder="Contact name"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Phone</Label>
                            <Input
                              value={formData.emergency_contact_phone}
                              onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })}
                              placeholder="Phone number"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Relationship</Label>
                            <Input
                              value={formData.emergency_contact_relationship}
                              onChange={(e) => setFormData({ ...formData, emergency_contact_relationship: e.target.value })}
                              placeholder="e.g., Spouse"
                            />
                          </div>
                        </div>
                      </div>
                    </TabsContent>

                    {/* Employment Tab */}
                    <TabsContent value="employment" className="space-y-4 mt-4">
                      {/* Vehicle */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Vehicle Signed Out</Label>
                          <Input
                            value={formData.vehicle_signed_out}
                            onChange={(e) => setFormData({ ...formData, vehicle_signed_out: e.target.value })}
                            placeholder="Vehicle ID or description"
                          />
                        </div>
                        <div className="flex items-center space-x-2 pt-8">
                          <Checkbox
                            id="personal_vehicle"
                            checked={formData.personal_vehicle}
                            onCheckedChange={(checked) => setFormData({ ...formData, personal_vehicle: checked })}
                          />
                          <Label htmlFor="personal_vehicle">Uses Personal Vehicle</Label>
                        </div>
                      </div>

                      {/* Certificates & Licenses */}
                      <div className="border rounded-lg p-4 space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Award className="h-4 w-4 text-muted-foreground" />
                            <h4 className="font-medium text-sm">Certificates & Licenses</h4>
                          </div>
                          <Button type="button" variant="outline" size="sm" onClick={addCertificate}>
                            <Plus className="h-4 w-4 mr-1" /> Add
                          </Button>
                        </div>
                        
                        {(formData.certificates || []).length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-4">
                            No certificates added. Click "Add" to add licenses or certifications.
                          </p>
                        ) : (
                          <div className="space-y-3">
                            {formData.certificates.map((cert, index) => (
                              <div key={cert.id} className="grid grid-cols-5 gap-2 items-end p-3 bg-muted/50 rounded-md">
                                <div className="space-y-1">
                                  <Label className="text-xs">Certificate/License Type</Label>
                                  <Input
                                    value={cert.type}
                                    onChange={(e) => updateCertificate(index, "type", e.target.value)}
                                    placeholder="e.g., IRATA, Mechanic License"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Number</Label>
                                  <Input
                                    value={cert.number}
                                    onChange={(e) => updateCertificate(index, "number", e.target.value)}
                                    placeholder="License #"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Issue Date</Label>
                                  <Input
                                    type="date"
                                    value={cert.issue_date}
                                    onChange={(e) => updateCertificate(index, "issue_date", e.target.value)}
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-xs">Expiry Date</Label>
                                  <Input
                                    type="date"
                                    value={cert.expiry_date}
                                    onChange={(e) => updateCertificate(index, "expiry_date", e.target.value)}
                                  />
                                </div>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => removeCertificate(index)}
                                  className="h-9 w-9"
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Other Employment Checkboxes */}
                      <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="work_visa"
                            checked={formData.work_visa}
                            onCheckedChange={(checked) => setFormData({ ...formData, work_visa: checked })}
                          />
                          <Label htmlFor="work_visa">Work Visa Required</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="direct_deposit"
                            checked={formData.direct_deposit}
                            onCheckedChange={(checked) => setFormData({ ...formData, direct_deposit: checked })}
                          />
                          <Label htmlFor="direct_deposit">Direct Deposit</Label>
                        </div>
                      </div>

                      {/* Pay & Benefits */}
                      <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                        <div className="space-y-2">
                          <Label>Hourly Rate ($)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={formData.hourly_rate}
                            onChange={(e) => setFormData({ ...formData, hourly_rate: parseFloat(e.target.value) || 0 })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Annual Salary ($)</Label>
                          <Input
                            type="number"
                            value={formData.salary}
                            onChange={(e) => setFormData({ ...formData, salary: parseFloat(e.target.value) || 0 })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Gas Allowance ($)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={formData.gas_allowance}
                            onChange={(e) => setFormData({ ...formData, gas_allowance: parseFloat(e.target.value) || 0 })}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-4 gap-4">
                        <div className="space-y-2">
                          <Label>Banked Hours</Label>
                          <Input
                            type="number"
                            step="0.5"
                            value={formData.banked_hours}
                            onChange={(e) => setFormData({ ...formData, banked_hours: parseFloat(e.target.value) || 0 })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Company Shares</Label>
                          <Input
                            type="number"
                            value={formData.company_shares}
                            onChange={(e) => setFormData({ ...formData, company_shares: parseFloat(e.target.value) || 0 })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Sick Days Used</Label>
                          <Input
                            type="number"
                            value={formData.sick_days_used}
                            onChange={(e) => setFormData({ ...formData, sick_days_used: parseInt(e.target.value) || 0 })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Sick Days Total</Label>
                          <Input
                            type="number"
                            value={formData.sick_days_total}
                            onChange={(e) => setFormData({ ...formData, sick_days_total: parseInt(e.target.value) || 0 })}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Vacation Days Used</Label>
                          <Input
                            type="number"
                            value={formData.vacation_days_used}
                            onChange={(e) => setFormData({ ...formData, vacation_days_used: parseInt(e.target.value) || 0 })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Vacation Days Total</Label>
                          <Input
                            type="number"
                            value={formData.vacation_days_total}
                            onChange={(e) => setFormData({ ...formData, vacation_days_total: parseInt(e.target.value) || 0 })}
                          />
                        </div>
                      </div>

                      {/* Banking Info */}
                      <div className="border rounded-lg p-4 space-y-4">
                        <h4 className="font-medium text-sm">Banking Information (for Direct Deposit)</h4>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Bank Name</Label>
                            <Input
                              value={formData.banking_info?.bank_name || ""}
                              onChange={(e) => setFormData({
                                ...formData,
                                banking_info: { ...formData.banking_info, bank_name: e.target.value }
                              })}
                              placeholder="e.g., TD Bank"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Account Holder</Label>
                            <Input
                              value={formData.banking_info?.account_holder || ""}
                              onChange={(e) => setFormData({
                                ...formData,
                                banking_info: { ...formData.banking_info, account_holder: e.target.value }
                              })}
                              placeholder="Name on account"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label>Institution #</Label>
                            <Input
                              value={formData.banking_info?.institution_number || ""}
                              onChange={(e) => setFormData({
                                ...formData,
                                banking_info: { ...formData.banking_info, institution_number: e.target.value }
                              })}
                              placeholder="3 digits"
                              maxLength={3}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Transit #</Label>
                            <Input
                              value={formData.banking_info?.transit_number || ""}
                              onChange={(e) => setFormData({
                                ...formData,
                                banking_info: { ...formData.banking_info, transit_number: e.target.value }
                              })}
                              placeholder="5 digits"
                              maxLength={5}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Account #</Label>
                            <Input
                              value={formData.banking_info?.account_number || ""}
                              onChange={(e) => setFormData({
                                ...formData,
                                banking_info: { ...formData.banking_info, account_number: e.target.value }
                              })}
                              placeholder="Account number"
                            />
                          </div>
                        </div>
                      </div>
                    </TabsContent>

                    {/* Personal Tab */}
                    <TabsContent value="personal" className="space-y-4 mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Date of Birth</Label>
                          <Input
                            type="date"
                            value={formData.date_of_birth}
                            onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>SIN (Sensitive)</Label>
                          <Input
                            value={formData.sin}
                            onChange={(e) => setFormData({ ...formData, sin: e.target.value })}
                            placeholder="Social Insurance Number"
                            type="password"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Allergies</Label>
                        <Textarea
                          value={formData.allergies}
                          onChange={(e) => setFormData({ ...formData, allergies: e.target.value })}
                          placeholder="Any known allergies..."
                          rows={2}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Medical Conditions</Label>
                        <Textarea
                          value={formData.medical_conditions}
                          onChange={(e) => setFormData({ ...formData, medical_conditions: e.target.value })}
                          placeholder="Any relevant medical conditions..."
                          rows={2}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Dietary Restrictions</Label>
                        <Textarea
                          value={formData.dietary_restrictions}
                          onChange={(e) => setFormData({ ...formData, dietary_restrictions: e.target.value })}
                          placeholder="Any dietary restrictions or preferences..."
                          rows={2}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Notes</Label>
                        <Textarea
                          value={formData.notes}
                          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                          placeholder="Additional notes..."
                          rows={3}
                        />
                      </div>

                      {/* Custom Sections */}
                      <div className="border rounded-lg p-4 space-y-4 mt-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Settings2 className="h-4 w-4 text-muted-foreground" />
                            <h4 className="font-medium text-sm">Custom Information Sections</h4>
                          </div>
                          <Button type="button" variant="outline" size="sm" onClick={addCustomSection}>
                            <Plus className="h-4 w-4 mr-1" /> Add Section
                          </Button>
                        </div>
                        
                        {(formData.custom_sections || []).length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-4">
                            No custom sections. Add sections for additional employee information categories.
                          </p>
                        ) : (
                          <div className="space-y-4">
                            {formData.custom_sections.map((section, sectionIndex) => (
                              <div key={section.id} className="border rounded-md p-3 space-y-3 bg-muted/30">
                                <div className="flex items-center gap-2">
                                  <Input
                                    value={section.name}
                                    onChange={(e) => updateSectionName(sectionIndex, e.target.value)}
                                    placeholder="Section Name (e.g., Training Records, Equipment Issued)"
                                    className="flex-1 font-medium"
                                  />
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => addFieldToSection(sectionIndex)}
                                    title="Add field"
                                  >
                                    <Plus className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => removeSection(sectionIndex)}
                                    title="Remove section"
                                  >
                                    <X className="h-4 w-4 text-destructive" />
                                  </Button>
                                </div>
                                
                                {(section.fields || []).length === 0 ? (
                                  <p className="text-xs text-muted-foreground pl-2">
                                    Click + to add fields to this section
                                  </p>
                                ) : (
                                  <div className="space-y-2 pl-2">
                                    {section.fields.map((field, fieldIndex) => (
                                      <div key={field.id} className="flex items-center gap-2">
                                        <Input
                                          value={field.label}
                                          onChange={(e) => updateSectionField(sectionIndex, fieldIndex, "label", e.target.value)}
                                          placeholder="Field name"
                                          className="w-1/3"
                                        />
                                        <Input
                                          value={field.value}
                                          onChange={(e) => updateSectionField(sectionIndex, fieldIndex, "value", e.target.value)}
                                          placeholder="Value"
                                          className="flex-1"
                                        />
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="icon"
                                          onClick={() => removeSectionField(sectionIndex, fieldIndex)}
                                          className="h-8 w-8"
                                        >
                                          <Trash2 className="h-3 w-3 text-muted-foreground" />
                                        </Button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </TabsContent>
                  </Tabs>

                <DialogFooter className="flex-col sm:flex-row gap-2">
                  {/* Phase 19.23 — Portal access toggle */}
                  {!editingEmployee && (
                    <div className="w-full mb-2 sm:mb-0 p-3 bg-muted/40 rounded-md flex items-start gap-3" data-testid="grant-portal-section">
                      <Checkbox
                        id="emp_grant_portal_access"
                        checked={formData.grant_portal_access}
                        onCheckedChange={(checked) =>
                          setFormData({
                            ...formData,
                            grant_portal_access: !!checked,
                            send_invite_now: checked ? autoSendInvitesDefault : null,
                          })
                        }
                        data-testid="grant-portal-checkbox"
                        className="mt-1"
                      />
                      <div className="flex-1 text-left">
                        <Label htmlFor="emp_grant_portal_access" className="cursor-pointer text-sm font-medium">
                          Grant employee portal access
                        </Label>
                        {formData.grant_portal_access && (
                          <div className="flex items-center gap-2 mt-1.5">
                            <Checkbox
                              id="emp_send_invite_now"
                              checked={!!formData.send_invite_now}
                              onCheckedChange={(checked) =>
                                setFormData({ ...formData, send_invite_now: !!checked })
                              }
                              data-testid="send-invite-now-checkbox"
                            />
                            <Label htmlFor="emp_send_invite_now" className="cursor-pointer text-xs">
                              Email the setup link now
                              {autoSendInvitesDefault && (
                                <span className="text-muted-foreground ml-1">(default ON)</span>
                              )}
                            </Label>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="flex gap-2 w-full sm:w-auto sm:ml-auto">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setDialogOpen(false)}
                      className="flex-1 sm:flex-initial"
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={saving} data-testid="save-employee-btn" className="flex-1 sm:flex-initial">
                      {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      {editingEmployee ? "Update" : "Create"}
                    </Button>
                  </div>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search employees..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="employee-search-input"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="current-only"
                checked={showCurrentOnly}
                onCheckedChange={setShowCurrentOnly}
              />
              <Label htmlFor="current-only">Current employees only</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="hidden md:table-cell">Email</TableHead>
                <TableHead className="hidden md:table-cell">Phone</TableHead>
                <TableHead className="hidden lg:table-cell">Hire Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="hidden sm:table-cell">Portal</TableHead>
                {isOfficeManager && <TableHead className="w-20">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEmployees.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={isOfficeManager ? 7 : 6}
                    className="text-center py-8 text-muted-foreground"
                  >
                    No employees found
                  </TableCell>
                </TableRow>
              ) : (
                filteredEmployees.map((emp) => (
                  <TableRow
                    key={emp.id}
                    data-testid={`employee-row-${emp.id}`}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/employees/${emp.id}`)}
                  >
                    <TableCell className="font-medium">
                      <span
                        className="hover:underline"
                        data-testid={`employee-name-${emp.id}`}
                      >
                        {emp.name}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {EMPLOYEE_TYPES.find((t) => t.value === emp.employee_type)
                          ?.label || emp.employee_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {emp.email}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {emp.phone || "-"}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {formatDate(emp.hire_date)}
                    </TableCell>
                    <TableCell>
                      {emp.is_current ? (
                        <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                          <UserCheck className="h-3 w-3 mr-1" />
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="secondary">
                          <UserX className="h-3 w-3 mr-1" />
                          Archived
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell" onClick={(e) => e.stopPropagation()}>
                      <PortalStatusBadge
                        portalStatus={emp.portal_status}
                        targetType="employee"
                        targetId={emp.id}
                        targetEmail={emp.email}
                        onChanged={fetchEmployees}
                      />
                    </TableCell>
                    {isOfficeManager && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenDialog(emp)}
                            data-testid={`edit-employee-${emp.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {emp.is_current && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(emp.id)}
                              className="text-destructive hover:text-destructive"
                              data-testid={`delete-employee-${emp.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      
      {/* Phase 19.23 — Bulk portal invite modal */}
      <BulkPortalInviteModal
        open={bulkInviteOpen}
        onClose={() => setBulkInviteOpen(false)}
        accountType="employee_portal"
        onSent={() => fetchEmployees()}
      />
    </div>
  );
};

export default Employees;
